import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Users, Loader2 } from 'lucide-react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Events = () => {
  useDocumentMeta({
    title: 'Events | Revs Vibes',
    description: 'Explore upcoming Revs Vibes events, judging appearances, attendance, and automotive gatherings connected to the brand and its community.',
    canonical: 'https://www.revsvibes.com/events',
    image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1200',
  });
  const { language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/events`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setEvents(data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const t = {
    en: { title: 'EVENTS', subtitle: 'Exclusive automotive gatherings and enthusiast meetups', expected: 'Expected', learnMore: 'Learn More' },
    es: { title: 'EVENTOS', subtitle: 'Reuniones automotrices exclusivas y encuentros de entusiastas', expected: 'Esperados', learnMore: 'Más Info' },
    pt: { title: 'EVENTOS', subtitle: 'Encontros automotivos exclusivos e meetups de entusiastas', expected: 'Esperados', learnMore: 'Saiba Mais' },
    fr: { title: 'ÉVÉNEMENTS', subtitle: 'Rassemblements automobiles exclusifs et rencontres de passionnés', expected: 'Attendus', learnMore: 'En savoir plus' },
    it: { title: 'EVENTI', subtitle: 'Raduni automobilistici esclusivi e incontri di appassionati', expected: 'Previsti', learnMore: 'Scopri di più' },
  };
  const tr = t[language] || t.en;

  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-96 bg-black text-white overflow-hidden clickable-card">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1600" alt="Events" className="w-full h-full object-cover opacity-40" />
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center px-4 max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="events-title">{tr.title}</h1>
            <p className="text-xl text-gray-300">{tr.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={32} /></div>
          ) : events.length === 0 ? (
            <p className="text-center text-gray-400 py-20">No upcoming events.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map(event => (
                <Card key={event.id} className="clickable-card group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300" data-testid={`event-card-${event.id}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3 text-black">{event.title}</h3>
                    {event.description && <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>}
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2"><Calendar size={14} /><span>{event.date}</span></div>
                      <div className="flex items-center gap-2"><MapPin size={14} /><span>{event.location}</span></div>
                      {event.attendees && <div className="flex items-center gap-2"><Users size={14} /><span>{tr.expected}: {event.attendees}</span></div>}
                    </div>
                    <Button variant="outline" className="w-full" data-testid={`event-learn-more-${event.id}`}>{tr.learnMore}</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Events;
